import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from persisted store
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.accessToken) {
          config.headers.Authorization = `Bearer ${state.accessToken}`;
        }
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      const authData = localStorage.getItem('neoshift-auth');
      if (authData) {
        try {
          const { state } = JSON.parse(authData);
          if (state?.refreshToken) {
            const response = await axios.post(`${API_URL}/auth/refresh-token`, {
              refreshToken: state.refreshToken
            });
            
            const { accessToken, refreshToken } = response.data;
            
            // Update stored tokens
            localStorage.setItem('neoshift-auth', JSON.stringify({
              state: { accessToken, refreshToken }
            }));
            
            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Clear auth data on refresh failure
          localStorage.removeItem('neoshift-auth');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Helper functions for common API calls
export const apiHelpers = {
  // Dashboards
  getDashboards: (params) => api.get('/dashboards', { params }),
  getDashboard: (id) => api.get(`/dashboards/${id}`),
  getDashboardWithCharts: (id) => api.get(`/dashboards/${id}/charts`),
  createDashboard: (data) => api.post('/dashboards', data),
  updateDashboard: (id, data) => api.put(`/dashboards/${id}`, data),
  deleteDashboard: (id) => api.delete(`/dashboards/${id}`),
  
  // Charts
  getCharts: (params) => api.get('/charts', { params }),
  getChart: (id) => api.get(`/charts/${id}`),
  getChartData: (id, filters) => api.get(`/charts/${id}/data`, { params: { filters: JSON.stringify(filters) } }),
  previewChartQuery: (data) => api.post('/charts/preview', data),
  createChart: (data) => api.post('/charts', data),
  updateChart: (id, data) => api.put(`/charts/${id}`, data),
  deleteChart: (id) => api.delete(`/charts/${id}`),
  
  // Data Sources
  getDataSources: (params) => api.get('/data-sources', { params }),
  getChartSources: (params) => api.get('/data-sources/chart-sources', { params }),
  getBigQueryTableSchema: (params) => api.get('/data-sources/bigquery-schema', { params }),
  getDataSource: (id) => api.get(`/data-sources/${id}`),
  getDataSourceSchema: (id) => api.get(`/data-sources/${id}/schema`),
  getDataSourceData: (id, params) => api.get(`/data-sources/${id}/data`, { params }),
  createDataSource: (data) => api.post('/data-sources', data),
  uploadFile: (formData) => api.post('/data-sources/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  testConnection: (id) => api.post(`/data-sources/${id}/test`),
  importData: (id, mode) => api.post(`/data-sources/${id}/import`, { mode }),
  getImportStatus: (importId) => api.get(`/data-sources/imports/${importId}/status`),
  updateSchedule: (id, schedule) => api.patch(`/data-sources/${id}/schedule`, { schedule }),
  deleteDataSource: (id) => api.delete(`/data-sources/${id}`),
  
  // AI
  getModels: () => api.get('/ai/models'),
  chat: (data) => api.post('/ai/chat', data, { timeout: 120000 }), // 2 minutes for AI with tool calls
  generateChartWithAI: (data) => api.post('/ai/generate-chart', data, { timeout: 120000 }), // AI chart builder
  getConversations: (params) => api.get('/ai/conversations', { params }),
  getConversation: (id) => api.get(`/ai/conversations/${id}`),
  deleteConversation: (id) => api.delete(`/ai/conversations/${id}`),
  
  // Users
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  
  // Companies
  getCompanies: (params) => api.get('/companies', { params }),
  getCompany: (id) => api.get(`/companies/${id}`),
  getCompanyStats: (id) => api.get(`/companies/${id}/stats`),
  updateCompany: (id, data) => api.put(`/companies/${id}`, data),
  createCompany: (data) => api.post('/companies', data),
  
  // Billing
  getUsage: () => api.get('/billing/usage'),
  getStorageUsage: () => api.get('/billing/storage'),
  getAITokenUsage: () => api.get('/billing/ai-tokens'),
  getSeatCount: () => api.get('/billing/seats'),
  
  // Marts (dbt Business Logic)
  getMarts: (params) => api.get('/marts', { params }),
  getMart: (id) => api.get(`/marts/${id}`),
  getMartSources: (params) => api.get('/marts/sources', { params }),
  createMart: (data) => api.post('/marts', data),
  updateMart: (id, data) => api.put(`/marts/${id}`, data),
  generateMart: (id) => api.post(`/marts/${id}/generate`),
  generateAllMarts: (params) => api.post('/marts/generate-all', {}, { params }),
  previewMartSql: (data) => api.post('/marts/preview-sql', data),
  duplicateMart: (id, data) => api.post(`/marts/${id}/duplicate`, data),
  deleteMart: (id) => api.delete(`/marts/${id}`),
  generateMartWithAI: (data) => api.post('/marts/ai-generate', data),
  
  // Metrics (Semantic Layer)
  generateMetricWithAI: (data) => api.post('/metrics/ai-generate', data)
};

